import {
    Box,
    Button,
    Divider,
    ListItemIcon,
    ListItemText,
    ListSubheader,
    MenuItem,
    Tooltip,
} from '@mui/material';
import { ChevronDown, Lock, LockOpen } from 'lucide-react';
import { useIntl } from 'react-intl';
import MenuCheck from '~/components/Menu/MenuCheck';
import { STATUS_MARK_PX } from '~/components/Menu/menuRowSizes';
import DisabledHint from '~/components/Tooltips/DisabledHint';
import { useMenuAnchor } from '~/hooks/useMenuAnchor';
import { BlendCandidate } from '../adapters/types';
import { RowLockMode } from '../layout/types';
import { READOUT_ACCENT, TRIGGER_CHEVRON_SIZE_PX } from '../chrome';
import ConsoleMenu from '../ConsoleMenu';
import { readOnlyStructuresThrough, RowLockState } from '../linking/rowLock';
import BlendBindingMark, { BlendBindingSummary } from './BlendBindingMark';

/** What a row offers on how it lies on the primary row: its state and the writes. */
export interface RowLockControls {
    readonly state: RowLockState;
    readonly onLock: (candidate: BlendCandidate) => void;
    readonly onUnlock: () => void;
    readonly onSetMode: (mode: RowLockMode) => void;
}

const MODES: ReadonlyArray<{
    readonly mode: RowLockMode;
    readonly nameId: string;
    readonly hintId: string;
}> = [
    {
        mode: 'geometry',
        nameId: 'rowLock.mode.geometry',
        hintId: 'rowLock.mode.geometryHint',
    },
    {
        mode: 'zoomAndPosition',
        nameId: 'rowLock.mode.zoomAndPosition',
        hintId: 'rowLock.mode.zoomAndPositionHint',
    },
];

/** Whether two candidates bind the same image through the same registration. */
function sameBinding(a: BlendCandidate, b: BlendCandidate): boolean {
    if (a.binding.kind === 'mapped' && b.binding.kind === 'mapped') {
        return a.binding.registrationId === b.binding.registrationId;
    }
    return a.binding.kind === b.binding.kind;
}

/**
 * Whether a row follows the primary row, as one control in its footer.
 *
 * It names how the row's image is placed on the primary image: the
 * registration and its approval status, or the same frame of reference,
 * whether the row follows through it or, independent, draws the structure
 * set through it for reading. An unapproved registration is marked, never
 * refused. The list offers each
 * registration that places the image, and the way back to an independent
 * row. A locked row also offers what it takes from the primary row: the
 * slice and crosshair alone, or the zoom and position too. A row whose
 * image cannot be placed stays, disabled, and says why.
 */
export default function RowLockControl({
    state,
    onLock,
    onUnlock,
    onSetMode,
}: RowLockControls) {
    const intl = useIntl();
    const menu = useMenuAnchor();
    if (state.kind === 'none') {
        return null;
    }
    const current = state.kind === 'locked' ? state.current : undefined;
    // The binding that places the structure set on the row: the one it
    // follows through, or the one its read-only structures are drawn through.
    const placing = current ?? readOnlyStructuresThrough(state);
    const candidates = state.kind === 'unavailable' ? [] : state.candidates;
    const mode = state.kind === 'locked' ? state.mode : undefined;
    const name = intl.formatMessage({
        id:
            mode === 'zoomAndPosition'
                ? 'rowLock.lockedZoomAndPosition'
                : current
                  ? 'rowLock.locked'
                  : 'rowLock.independent',
    });
    const disabledReason =
        state.kind === 'unavailable'
            ? intl.formatMessage({ id: 'rowLock.unavailable' })
            : undefined;
    const summary = placing ? (
        <BlendBindingSummary
            binding={placing.binding}
            registrationLabel={placing.registrationLabel}
        />
    ) : null;
    const tooltip = current ? (
        summary
    ) : (
        <>
            {intl.formatMessage({ id: 'rowLock.independentHint' })}
            {summary ? (
                <Box component="span" sx={{ display: 'block', mt: 0.5 }}>
                    {summary}
                </Box>
            ) : null}
        </>
    );
    return (
        <>
            <DisabledHint
                title={disabledReason}
                placement="top"
                keepWrapper
                spanStyle={{ display: 'inline-flex' }}
            >
                <Tooltip
                    title={disabledReason ? '' : tooltip}
                    placement="top"
                    describeChild
                >
                    <Button
                        variant="picker"
                        aria-label={intl.formatMessage(
                            { id: 'rowLock.name' },
                            { state: name }
                        )}
                        aria-haspopup="menu"
                        aria-expanded={menu.open}
                        disabled={disabledReason !== undefined}
                        data-testid="row-lock"
                        data-lock-state={state.kind}
                        onClick={(event) => menu.onOpen(event.currentTarget)}
                        startIcon={
                            current ? (
                                <Lock size={12} color={READOUT_ACCENT} />
                            ) : (
                                <LockOpen size={12} />
                            )
                        }
                        endIcon={<ChevronDown size={TRIGGER_CHEVRON_SIZE_PX} />}
                    >
                        {placing ? (
                            <Box
                                component="span"
                                sx={{ display: 'flex', mr: 0.5 }}
                            >
                                <BlendBindingMark
                                    binding={placing.binding}
                                    sizePx={12}
                                />
                            </Box>
                        ) : null}
                        {name}
                    </Button>
                </Tooltip>
            </DisabledHint>
            <ConsoleMenu
                anchorEl={menu.anchorEl}
                open={menu.open}
                onClose={menu.onClose}
                minWidth={264}
            >
                <ListSubheader>
                    {intl.formatMessage({ id: 'rowLock.section' })}
                </ListSubheader>
                {candidates.map((candidate, index) => {
                    const isCurrent =
                        current !== undefined &&
                        sameBinding(current, candidate);
                    return (
                        <MenuItem
                            key={`lock-${index}`}
                            dense
                            selected={isCurrent}
                            data-testid="row-lock-through"
                            onClick={() => {
                                menu.onClose();
                                if (!isCurrent) {
                                    onLock(candidate);
                                }
                            }}
                        >
                            <ListItemIcon>
                                <BlendBindingMark
                                    binding={candidate.binding}
                                    sizePx={STATUS_MARK_PX}
                                />
                            </ListItemIcon>
                            <ListItemText>
                                <BlendBindingSummary
                                    binding={candidate.binding}
                                    registrationLabel={
                                        candidate.registrationLabel
                                    }
                                />
                            </ListItemText>
                            <MenuCheck selected={isCurrent} />
                        </MenuItem>
                    );
                })}
                {mode ? (
                    <>
                        <Divider />
                        <ListSubheader>
                            {intl.formatMessage({ id: 'rowLock.modeSection' })}
                        </ListSubheader>
                        {MODES.map((option) => (
                            <MenuItem
                                key={option.mode}
                                dense
                                selected={option.mode === mode}
                                data-testid={`row-lock-mode-${option.mode}`}
                                onClick={() => {
                                    menu.onClose();
                                    if (option.mode !== mode) {
                                        onSetMode(option.mode);
                                    }
                                }}
                            >
                                <ListItemText
                                    primary={intl.formatMessage({
                                        id: option.nameId,
                                    })}
                                    secondary={intl.formatMessage({
                                        id: option.hintId,
                                    })}
                                />
                                <MenuCheck selected={option.mode === mode} />
                            </MenuItem>
                        ))}
                    </>
                ) : null}
                <Divider />
                <MenuItem
                    dense
                    selected={!current}
                    data-testid="row-lock-independent"
                    onClick={() => {
                        menu.onClose();
                        if (current) {
                            onUnlock();
                        }
                    }}
                >
                    <ListItemIcon>
                        <LockOpen size={STATUS_MARK_PX} />
                    </ListItemIcon>
                    <ListItemText>
                        {intl.formatMessage({ id: 'rowLock.independent' })}
                    </ListItemText>
                    <MenuCheck selected={!current} />
                </MenuItem>
            </ConsoleMenu>
        </>
    );
}
